import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import BookingStepper from "@/features/booking/components/BookingStepper";
import StepDates from "@/features/booking/components/StepDates";
import StepRooms from "@/features/booking/components/StepRooms";
import StepAddons from "@/features/booking/components/StepAddons";
import StepGuestDetails from "@/features/booking/components/StepGuestDetails";
import StepPayment from "@/features/booking/components/StepPayment";
import { useBooking } from "@/features/booking/hooks/useBooking";
import type {
  GuestDetails,
  SelectedAddon,
  PriceBreakdown,
} from "@/types/booking";
import type { MealPlan } from "@/types/rooms";

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const {
    draft,
    step,
    updateDates,
    selectRoom,
    selectAddons,
    setGuestDetails,
    goNext,
    goBack,
  } = useBooking();

  useEffect(() => {
    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");
    const adults = Number(searchParams.get("adults")) || 2;
    const children = Number(searchParams.get("children")) || 0;
    if (checkIn && checkOut) updateDates(checkIn, checkOut, adults, children);
  }, []);

  const handleDatesNext = (
    checkIn: string,
    checkOut: string,
    adults: number,
    children: number,
  ) => {
    updateDates(checkIn, checkOut, adults, children);
    goNext();
  };

  const handleRoomSelect = (
    roomTypeId: string,
    roomTypeName: string,
    mealPlan: MealPlan,
    pricing: PriceBreakdown,
  ) => {
    selectRoom(roomTypeId, roomTypeName, mealPlan, pricing);
  };

  const handleAddonsNext = (addons: SelectedAddon[]) => {
    selectAddons(addons);
    goNext();
  };

  const handleGuestNext = (guest: GuestDetails) => {
    setGuestDetails(guest);
    goNext();
  };

  return (
    <div className="min-h-screen bg-metro-cream">
      <div className="bg-white border-b border-gray-100">
        <div className="container-narrow py-4">
          <p className="font-serif text-xl tracking-widest text-metro-green-950">
            METROPOLIS
          </p>
        </div>
      </div>

      <div className="container-narrow py-10">
        <div className="mb-10">
          <BookingStepper currentStep={step} />
        </div>

        <div className="mx-auto max-w-4xl rounded-2xl bg-white p-6 shadow-card md:p-10">
          {step === 1 && <StepDates draft={draft} onNext={handleDatesNext} />}
          {step === 2 && (
            <StepRooms
              draft={draft}
              onSelect={handleRoomSelect}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 3 && (
            <StepAddons
              draft={draft}
              onNext={handleAddonsNext}
              onBack={goBack}
            />
          )}
          {step === 4 && (
            <StepGuestDetails
              draft={draft}
              onNext={handleGuestNext}
              onBack={goBack}
            />
          )}
          {step === 5 && <StepPayment draft={draft} onBack={goBack} />}
        </div>
      </div>
    </div>
  );
}